import { createFileRoute } from '@tanstack/react-router'
import { Header } from '@/components/ui/header'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { z } from 'zod'
import { Input } from '@/components/ui/input'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from '@/components/ui/form'
import { useEffect, useState } from 'react'
import { PAYMENT_URL } from '@/api'
import { Route as ContractRoute } from './route'

export const Route = createFileRoute('/contracts/$houseId/$flat/payment')({
  component: PaymentPage,
})

const formSchema = z.object({
  sum: z.string().min(1, { message: 'Необходимо ввести сумму' }),
})

type FormData = z.infer<typeof formSchema>

function PaymentPage() {
  const { payment, id } = ContractRoute.useLoaderData()
  const params = Route.useParams()

  const [selectedOption, setSelectedOption] = useState<
    null | 'current' | 'currentPlusNext'
  >(null)

  const form = useForm<FormData>({
    defaultValues: {
      sum: '',
    },
    mode: 'onSubmit',
    resolver: zodResolver(formSchema),
  })

  useEffect(() => {
    if (selectedOption === 'current') {
      form.setValue('sum', String(payment), { shouldValidate: true })
    } else if (selectedOption === 'currentPlusNext') {
      form.setValue('sum', String(payment * 2), { shouldValidate: true })
    }
  }, [form, payment, selectedOption])

  const onSubmit = ({ sum }: FormData) => {
    const url = new URL(PAYMENT_URL)
    url.searchParams.set('cid', id)
    url.searchParams.set('amount', sum)
    url.searchParams.set('redirect', 'ufa')

    window.location.href = url.toString()
  }

  return (
    <>
      <Header
        title={'Сумма платежа'}
        linkProps={{ to: '/contracts/$houseId/$flat', params: params }}
      />
      <p>
        Выберите период, за который хотите внести оплату, либо введите сумму для
        оплаты самостоятельно
      </p>
      <div className="mt-3 flex h-11 items-center space-x-4.5">
        <Checkbox
          id="current"
          checked={selectedOption === 'current'}
          onCheckedChange={(checked) =>
            setSelectedOption(checked ? 'current' : null)
          }
        />
        <label htmlFor="current" className="leading-none">
          За текущий месяц
        </label>
      </div>
      <div className="mt-3 flex h-11 items-center space-x-4.5">
        <Checkbox
          id="currentPlusNext"
          checked={selectedOption === 'currentPlusNext'}
          onCheckedChange={(checked) =>
            setSelectedOption(checked ? 'currentPlusNext' : null)
          }
        />
        <label htmlFor="currentPlusNext" className="leading-none">
          За текущий месяц + 1 следующий
        </label>
      </div>
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className={'mt-10 flex flex-1 flex-col'}
        >
          <FormField
            control={form.control}
            name={'sum'}
            render={({ field }) => (
              <FormItem>
                <FormLabel>Сумма платежа, ₽</FormLabel>
                <FormControl>
                  <Input
                    placeholder={'Сумма, ₽'}
                    type={'number'}
                    className={'w-1/2'}
                    {...field}
                    onChange={(e) => {
                      setSelectedOption(null)
                      field.onChange(e)
                    }}
                  />
                </FormControl>
              </FormItem>
            )}
          />
          <Button
            type={'submit'}
            className={'mt-auto'}
            disabled={!form.formState.isValid}
          >
            Оплатить
          </Button>
        </form>
      </Form>
    </>
  )
}
